import type { PropertiesVersion, TrackerOptions } from '@mathflat/data-tracking'
import { deviceType, isElectron } from 'react-device-detect'

import { localStorageService } from '@/service/storage.service'
import { envUtils } from '@/utils/envUtils'

export const GLOBAL_PROPERTIES_VERSION: PropertiesVersion = 'v1.0@2025-07-30'
export const USER_PROPERTIES_VERSION: PropertiesVersion = 'v1.0@2025-07-30'

type BuildTeacherMixpanelConfigParams = {
  debug: boolean
  isElectronApp: boolean
  isLive: boolean
}

export const isElectronApp = () => isElectron || window.bridge !== undefined
export const buildTeacherMixpanelConfig = ({
  debug,
  isElectronApp,
  isLive,
}: BuildTeacherMixpanelConfigParams): NonNullable<TrackerOptions['mixpanel']['config']> => ({
  track_pageview: true,
  persistence: 'localStorage',
  cookie_name: 'version3',
  cross_subdomain_cookie: false,
  autocapture: {
    click: false,
    // `/` 는 화면 없이 항상 다른 경로로 넘기는 경로(`/` 와 맞는 라우트가 없어 LoginFallbackRedirect 로 넘어감)라 `/` 페이지뷰는 의미가 없다.
    // init 이 리다이렉트보다 먼저 끝나면 `/` 페이지뷰가 남지만, 라이브 데이터를 유지하려고 지금은 막지 않는다.
    // 막아야 하면 아래 한 줄을 넣는다 — 넘어간 뒤의 페이지뷰와 수기 이벤트는 그대로 나간다.
    // block_url_regexes: [/^https?:\/\/[^/]+\/?(?:[?#].*)?$/],
    pageview: 'url-with-path',
    scroll: true,
    submit: false,
    input: false,
    capture_text_content: false,
  },
  app_host: window.location.hostname,
  debug,
  batch_requests: isLive,
  record_sessions_percent: isElectronApp ? 0 : 10,
  record_mask_text_selector: '.mp-mask',
})

// 한도 위반 보고는 사용자 콘솔에 남기지 않고, MIXPANEL_DEBUG 를 켠 경우에만 패키지 기본값(console.error)으로 본다
const ignoreInvalidPayload = () => undefined

export const buildTeacherTrackerOptions = (): TrackerOptions => {
  const token = import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN
  const debug = !!localStorageService.getItem('MIXPANEL_DEBUG')
  return {
    mixpanel: {
      token: token ?? '',
      config: {
        ...buildTeacherMixpanelConfig({
          debug,
          isLive: envUtils.isLive,
          isElectronApp: isElectronApp(),
        }),
        // 버저닝 이전에 객체째 등록하던 옛 super property 를 지운다. 지금 값은 academy_·teacher_ 접두 키라 겹치지 않는다
        loaded: (mixpanel) => {
          if (mixpanel.get_property('globalPropertiesVersion')) return
          mixpanel.unregister('academy')
          mixpanel.unregister('coupons')
          mixpanel.unregister('teacher')

          // TODO: 잘못 들어간 대량의 데이터 삭제
          // 사실은 mixpanel에 api를 쏴서 전역적으로 삭제해야하는데
          // 임시로 클라이언트에서 조치하려면 이렇게 해야함 (실제 실행한 적 없음)
          // this.removeUserProperty(['columnDefs', '_subscribeItems', 'statusHistories'])
        },
      },
    },
    enabled: !!token,
    send: true,
    // 첫 렌더를 막지 않게 init 을 브라우저가 한가할 때로 미룬다
    initOnIdle: true,
    context: {
      $device_type: deviceType,
      $user_agent: navigator.userAgent,
      releaseId: envUtils.releaseId,
    },
    onInvalid: debug ? undefined : ignoreInvalidPayload,
  }
}
